import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { X, ChevronRight, ChevronLeft, Sparkles } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { getMainTourItems, TOUR_VERSION } from '../config/navRegistry';
import { API_BASE } from '../config/apiBase';
import { OverlayQueue, OVERLAY_PRIORITY } from '../utils/overlayQueue';

// ==================== UTILITIES ====================

const isMobile = () => window.innerWidth < 768;
const sleep = ms => new Promise(r => setTimeout(r, ms));
const API = API_BASE;

function waitForEl(sel, timeout = 2500) {
    return new Promise(resolve => {
        const el = document.querySelector(sel);
        if (el) return resolve(el);
        const obs = new MutationObserver(() => {
            const found = document.querySelector(sel);
            if (found) { obs.disconnect(); resolve(found); }
        });
        obs.observe(document.body, { childList: true, subtree: true });
        setTimeout(() => { obs.disconnect(); resolve(null); }, timeout);
    });
}

function boostElZIndex(selector) {
    const el = document.querySelector(selector);
    if (!el) return [];
    const boosted = [];
    const wrapper = el.closest('[data-radix-popper-content-wrapper]');
    if (wrapper) { wrapper.style.zIndex = '100001'; boosted.push(wrapper); }
    const menu = el.closest('[role="menu"]');
    if (menu && !boosted.includes(menu)) { menu.style.zIndex = '100001'; boosted.push(menu); }
    return boosted;
}

function resetBoosted(els) { (els || []).forEach(el => { try { el.style.zIndex = ''; } catch {} }); }
function closeDropdowns() { document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', code: 'Escape', bubbles: true, cancelable: true })); }
function forceOpenDropdown() { window.dispatchEvent(new CustomEvent('sv_force_avatar_dropdown', { detail: { open: true } })); }
function forceCloseDropdown() { window.dispatchEvent(new CustomEvent('sv_force_avatar_dropdown', { detail: { open: false } })); }
function setZIndex(selector, z) { const el = document.querySelector(selector); if (el) el.style.zIndex = z; }

function ensureSidebarOpen() {
    const sidebar = document.querySelector('[data-testid="system-sidebar"]');
    if (!sidebar || sidebar.classList.contains('open')) return;
    const btn = document.querySelector('[data-testid="system-hamburger"]');
    if (btn) btn.click();
}

function ensureSidebarClosed() {
    const sidebar = document.querySelector('[data-testid="system-sidebar"]');
    if (!sidebar || !sidebar.classList.contains('open')) return;
    const btn = document.querySelector('[data-testid="system-hamburger"]');
    if (btn) btn.click();
}

// ==================== STORAGE ====================

const SK_SITE = 'sv_tour_site_done';
const TOUR_DONE_EVENT = 'sv_tour_completed';

// ==================== TOOLTIP COMPONENT ====================

function TourTooltip({ steps, currentStep, onNext, onPrev, onClose, onFinish }) {
    const tooltipRef = useRef(null);
    const [pos, setPos] = useState({ top: '50%', left: 16, placement: 'center' });
    const [ready, setReady] = useState(false);
    const prevElRef = useRef(null);
    const boostedRef = useRef([]);
    const step = steps[currentStep];

    const cleanup = useCallback(() => {
        if (prevElRef.current) {
            try {
                prevElRef.current.style.zIndex = '';
                prevElRef.current.style.boxShadow = '';
                prevElRef.current.style.position = '';
                prevElRef.current.style.borderRadius = '';
            } catch {}
            prevElRef.current = null;
        }
        resetBoosted(boostedRef.current);
        boostedRef.current = [];
        setZIndex('[data-testid="system-sidebar"]', '');
        setZIndex('[data-testid="system-bottom-nav"]', '');
    }, []);

    useEffect(() => {
        if (!step) return;
        let cancelled = false;
        setReady(false);

        const run = async () => {
            cleanup();
            const mobile = isMobile();

            if (step.before) {
                try { await step.before(); } catch (e) { console.warn('[Tour before]', e); }
                if (cancelled) return;
                await sleep(200);
                if (cancelled) return;
            }

            const targetSel = mobile
                ? (step.mobileTarget !== undefined ? step.mobileTarget : step.target)
                : (step.desktopTarget || step.target);
            const isVirtual = step.virtual || !targetSel;

            if (isVirtual) {
                setPos({
                    top: mobile ? Math.max(window.innerHeight / 2 - 120, 80) : window.innerHeight / 2 - 120,
                    left: mobile ? 16 : Math.max(window.innerWidth / 2 - 170, 16),
                    placement: 'center',
                });
                setReady(true);
                return;
            }

            const el = await waitForEl(targetSel, 3000);
            if (cancelled) return;

            if (!el) {
                setPos({
                    top: mobile ? Math.max(window.innerHeight / 2 - 120, 80) : window.innerHeight / 2 - 120,
                    left: mobile ? 16 : Math.max(window.innerWidth / 2 - 170, 16),
                    placement: 'center',
                });
                setReady(true);
                return;
            }

            if (step.boostSidebar) setZIndex('[data-testid="system-sidebar"]', '100001');
            if (step.boostBottomNav) setZIndex('[data-testid="system-bottom-nav"]', '100001');
            if (step.boostPortal) boostedRef.current = boostElZIndex(targetSel);

            const rect = el.getBoundingClientRect();
            if (rect.top < 60 || rect.bottom > window.innerHeight - 60) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                await sleep(350);
                if (cancelled) return;
            }

            el.style.position = 'relative';
            el.style.zIndex = '100001';
            el.style.boxShadow = '0 0 0 4px rgba(var(--sv-accent-rgb), 0.45), 0 0 20px rgba(var(--sv-accent-rgb), 0.15)';
            el.style.borderRadius = '8px';
            prevElRef.current = el;

            const r = el.getBoundingClientRect();
            const tH = 210, tW = mobile ? window.innerWidth - 32 : 340, pad = 14;
            let top, left, placement;

            // Safety: if element is in a floating portal/dropdown near screen edge,
            // fall back to centered position
            const isOffscreen = r.width === 0 || r.height === 0 || r.right < 10 || r.bottom < 10 || r.left > window.innerWidth - 10 || r.top > window.innerHeight - 10;
            if (isOffscreen) {
                top = mobile ? Math.max(window.innerHeight / 2 - 120, 80) : window.innerHeight / 2 - 120;
                left = mobile ? 16 : Math.max(window.innerWidth / 2 - 170, 16);
                placement = 'center';
                setPos({ top, left, placement });
                setReady(true);
                return;
            }

            if (mobile) {
                left = 16;
                const spaceBelow = window.innerHeight - r.bottom - pad;
                const spaceAbove = r.top - pad;
                if (spaceBelow >= tH + 10) { top = r.bottom + pad; placement = 'below'; }
                else if (spaceAbove >= tH + 10) { top = r.top - tH - pad; placement = 'above'; }
                else {
                    top = r.top > window.innerHeight / 2
                        ? Math.max(10, r.top - tH - 10)
                        : Math.min(r.bottom + 10, window.innerHeight - tH - 10);
                    placement = r.top > window.innerHeight / 2 ? 'above' : 'below';
                }
                top = Math.max(10, Math.min(top, window.innerHeight - tH - 10));
            } else {
                // Decide lado pelo CENTRO do elemento vs centro da tela (mais confiavel
                // que medir distancia da borda, que falha em telas pequenas).
                const elCenterX = r.left + r.width / 2;
                const screenMid = window.innerWidth / 2;
                const isRightHalf = elCenterX > screenMid + 40;
                const isLeftHalf = elCenterX < screenMid - 40;
                const isTopArea = r.top < 120; // navbar fixo

                if (isTopArea) {
                    // Navbar: popup ABAIXO, alinhado com o elemento
                    top = r.bottom + pad;
                    placement = 'below';
                    if (isRightHalf) {
                        left = r.right - tW;          // alinha borda direita
                    } else if (isLeftHalf) {
                        left = r.left;                // alinha borda esquerda
                    } else {
                        left = elCenterX - tW / 2;    // centraliza
                    }
                } else {
                    // Meio/baixo da tela: popup ao LADO oposto do elemento
                    if (isRightHalf) {
                        left = r.left - tW - pad;
                        placement = 'left';
                        top = r.top + r.height / 2 - tH / 2;
                    } else if (isLeftHalf) {
                        left = r.right + pad;
                        placement = 'right';
                        top = r.top + r.height / 2 - tH / 2;
                    } else {
                        // elemento centralizado: popup abaixo (ou acima se nao couber)
                        if (window.innerHeight - r.bottom - pad >= tH) {
                            top = r.bottom + pad; placement = 'below';
                        } else {
                            top = r.top - tH - pad; placement = 'above';
                        }
                        left = elCenterX - tW / 2;
                    }
                }
                // Clamp final dentro do viewport
                if (left + tW > window.innerWidth - 10) left = window.innerWidth - tW - 10;
                if (left < 10) left = 10;
                if (top + tH > window.innerHeight - 10) top = window.innerHeight - tH - 10;
                if (top < 10) top = 10;
            }

            setPos({ top, left, placement });
            setReady(true);
        };

        run();
        return () => { cancelled = true; };
    }, [currentStep, cleanup, steps]);

    useEffect(() => () => { cleanup(); forceCloseDropdown(); closeDropdowns(); }, [cleanup]);

    if (!step) return null;

    const isLast = currentStep === steps.length - 1;
    const isFinal = step.isFinal;
    const mobile = isMobile();

    const doClose = () => { cleanup(); closeDropdowns(); if (mobile) ensureSidebarClosed(); onClose(); };
    const doNext = () => {
        if (isLast || isFinal) { cleanup(); closeDropdowns(); if (mobile) ensureSidebarClosed(); onFinish ? onFinish() : onClose(); return; }
        setReady(false); onNext();
    };
    const doPrev = () => { setReady(false); onPrev(); };

    return (
        <>
            <div style={{ position: 'fixed', inset: 0, zIndex: 100000, background: step.virtual ? 'rgba(0,0,0,0.75)' : 'rgba(0,0,0,0.55)', transition: 'background 0.3s ease' }} onClick={doClose} data-testid="tour-overlay" />
            <div ref={tooltipRef} style={{
                position: 'fixed', zIndex: 100002, top: pos.top, left: pos.left,
                background: '#0D2C63', border: '1px solid rgba(140,180,255,0.30)', borderRadius: 16, padding: '20px 22px',
                maxWidth: mobile ? 'calc(100vw - 32px)' : 360, width: mobile ? 'calc(100vw - 32px)' : 340,
                boxShadow: '0 16px 48px rgba(0,0,0,0.7)',
                transition: 'top 0.25s ease, left 0.25s ease, opacity 0.2s ease',
                opacity: ready ? 1 : 0, pointerEvents: ready ? 'auto' : 'none',
            }} onClick={(e) => e.stopPropagation()} data-testid="tour-tooltip">
                {!isFinal && (
                    <button onClick={doClose} style={{ position: 'absolute', top: 12, right: 12, background: 'none', border: 'none', color: '#555', cursor: 'pointer', padding: 4 }} data-testid="tour-close-btn">
                        <X size={14} />
                    </button>
                )}
                {isFinal && (
                    <div className="flex justify-center mb-3">
                        <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: 'rgba(var(--sv-accent-rgb), 0.12)', border: '1px solid rgba(var(--sv-accent-rgb), 0.2)' }}>
                            <Sparkles size={24} style={{ color: 'var(--sv-accent)' }} />
                        </div>
                    </div>
                )}
                <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--sv-accent)', marginBottom: 8 }}>{step.title}</div>
                <div style={{ fontSize: 13, color: '#E5E5E5', lineHeight: 1.75, marginBottom: 18, whiteSpace: 'pre-line' }}>{step.content}</div>
                <div className="flex items-center justify-between">
                    {!isFinal ? <span style={{ fontSize: 11, color: '#555' }}>{currentStep + 1} / {steps.length}</span> : <span />}
                    <div className="flex gap-2">
                        {currentStep > 0 && !isFinal && (
                            <button onClick={doPrev} style={{ background: 'none', color: '#999', border: 'none', fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, padding: '8px 12px' }}>
                                <ChevronLeft size={14} /> Voltar
                            </button>
                        )}
                        <button onClick={doNext} style={{
                            background: 'var(--sv-secondary)', color: 'var(--sv-on-secondary)', border: 'none', borderRadius: 12,
                            padding: isFinal ? '12px 28px' : '10px 18px',
                            fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, minHeight: 44,
                        }} data-testid="tour-next-btn">
                            {isFinal ? step.buttonText || 'Comecar agora' : isLast ? 'Concluir' : 'Proximo'} {!isLast && !isFinal && <ChevronRight size={14} />}
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
}

// ==================== SITE TOUR STEPS ====================

function buildSiteSteps(navigate, isAuthenticated, canAccessSystem) {
    const mobile = isMobile();
    const steps = [
        {
            target: '[data-testid="navbar-logo"]',
            mobileTarget: '[data-testid="navbar-logo-mobile"]',
            title: 'Bem-vindo ao SuperVitrine!',
            content: 'Aqui voce encontra as melhores ofertas da internet com curadoria e precos verificados. Vamos conhecer a plataforma!',
            before: async () => { closeDropdowns(); if (window.location.pathname !== '/') { navigate('/'); await sleep(600); } window.scrollTo({ top: 0, behavior: 'smooth' }); },
        },
        {
            target: '[data-testid="search-input"]',
            mobileTarget: '[data-testid="mobile-search-input"]',
            title: 'Busca Inteligente',
            content: 'Pesquise por produto, loja ou categoria. O sistema encontra as ofertas mais relevantes para voce.',
            before: async () => { closeDropdowns(); if (window.location.pathname !== '/') { navigate('/'); await sleep(600); } window.scrollTo({ top: 0, behavior: 'smooth' }); },
        },
        {
            target: '[data-testid="deals-feed"]',
            title: 'Feed de Ofertas',
            content: 'Navegue pelas ofertas em destaque. Filtre por recentes, em alta ou menor preco.',
            before: async () => { closeDropdowns(); if (window.location.pathname !== '/') { navigate('/'); await sleep(600); } const feed = document.querySelector('[data-testid="deals-feed"]'); if (feed) { feed.scrollIntoView({ behavior: 'smooth', block: 'center' }); await sleep(400); } },
        },
        {
            target: '[data-testid="publish-btn"]',
            mobileTarget: '[data-testid="mobile-publish-btn"]',
            title: 'Publicar Oferta',
            content: 'Encontrou uma promocao boa? Compartilhe com a comunidade!',
            before: async () => { closeDropdowns(); if (window.location.pathname !== '/') { navigate('/'); await sleep(600); } window.scrollTo({ top: 0, behavior: 'smooth' }); await sleep(200); },
        },
        {
            target: '[data-testid="nav-planos"]',
            mobileTarget: null,
            title: 'Planos e Assinatura',
            content: mobile
                ? 'Planos do SuperVitrine! Empresas desbloqueiam Ponto Digital, relatorios automaticos e muito mais.'
                : 'Conheca os planos! Empresas desbloqueiam Ponto Digital, relatorios e gestao de equipe.',
            before: async () => { closeDropdowns(); if (mobile) { navigate('/planos'); await sleep(800); window.scrollTo({ top: 0, behavior: 'smooth' }); } },
        },
    ];

    if (isAuthenticated) {
        steps.push({
            target: '[data-testid="profile-card"]',
            title: 'Seu Perfil',
            content: 'Sua pagina de perfil! Acompanhe nivel, pontos, ofertas publicadas e conquistas.',
            before: async () => { forceCloseDropdown(); if (window.location.pathname !== '/perfil') { navigate('/perfil'); await sleep(800); } window.scrollTo({ top: 0, behavior: 'smooth' }); await sleep(300); },
        });
        steps.push({
            target: '[data-testid="user-menu-trigger"]',
            mobileTarget: '[data-testid="mobile-avatar-btn"]',
            title: 'Menu do Avatar',
            content: 'Toque no avatar para acessar perfil, desejos, cupons e configuracoes.',
            before: async () => { forceCloseDropdown(); if (window.location.pathname !== '/') { navigate('/'); await sleep(800); } window.scrollTo({ top: 0, behavior: 'smooth' }); await sleep(200); },
        });
        if (canAccessSystem) {
            steps.push({
                target: '[data-testid="menu-painel-gestao"]',
                mobileTarget: '[data-testid="mobile-menu-painel-gestao"]',
                boostPortal: true,
                title: 'Painel de Gestao',
                content: 'Voce tem acesso ao sistema interno!\n\nSelecione "Painel de Gestao" para gerenciar sua empresa.',
                before: async () => { if (window.location.pathname !== '/') { navigate('/'); await sleep(800); } window.scrollTo({ top: 0, behavior: 'smooth' }); await sleep(300); forceOpenDropdown(); await sleep(500); },
            });
        }
    }

    steps.push({
        virtual: true, isFinal: true,
        title: 'Seja bem-vindo!',
        content: 'Explore as ofertas, publique promocoes e aproveite todos os recursos do SuperVitrine.\n\nQualquer duvida, estamos a disposicao!',
        buttonText: 'Comecar agora',
        before: async () => { forceCloseDropdown(); closeDropdowns(); await sleep(100); if (window.location.pathname !== '/') { navigate('/'); await sleep(600); } },
    });

    return steps;
}

// ==================== DYNAMIC SYSTEM TOUR BUILDER ====================

function buildSystemTourSteps(profile) {
    const mobile = isMobile();
    const navItems = getMainTourItems(profile);
    const steps = [];

    // Step 0 (mobile only): hamburger
    if (mobile) {
        steps.push({
            target: '[data-testid="system-hamburger"]',
            title: 'Menu de Navegacao',
            content: profile === 'empresa'
                ? 'Toque aqui para abrir o menu lateral e acessar todas as funcoes da sua empresa.'
                : profile === 'colaborador'
                    ? 'Toque aqui para abrir o menu lateral e ver todas as suas opcoes.'
                    : 'Toque aqui para abrir o menu lateral com todas as secoes do sistema.',
            before: async () => { ensureSidebarClosed(); await sleep(300); },
        });
    }

    // Dynamic steps from nav registry
    for (const item of navItems) {
        const targetSel = `[data-testid="nav-${item.key}"]`;
        const step = {
            target: targetSel,
            title: item.label,
            content: item.tourDesc,
        };

        if (mobile) {
            // Sidebar nav items need sidebar open
            step.boostSidebar = true;
            step.before = async () => { ensureSidebarOpen(); await sleep(400); };
        } else {
            step.before = async () => { await sleep(100); };
        }

        steps.push(step);
    }

    // Bottom nav / special items
    if (mobile) {
        steps.push({
            target: '[data-testid="bottom-nav-conta"]',
            title: 'Seu Perfil',
            content: profile === 'empresa'
                ? 'Acesse seu perfil e acompanhe pontos e nivel na plataforma!'
                : profile === 'colaborador'
                    ? 'Acesse seu perfil e altere dados pessoais.'
                    : 'Acesse seu perfil aqui. Acompanhe pontos e evolua de nivel!',
            boostBottomNav: true,
            before: async () => { ensureSidebarClosed(); await sleep(300); },
        });
    }

    // Go to site step (desktop only in some cases)
    steps.push({
        target: '[data-testid="go-to-site"]',
        title: 'Voltar ao Site',
        content: profile === 'empresa'
            ? 'Acesse o site publico ou retorne ao Painel de Gestao pelo menu do avatar.'
            : 'Este botao leva voce de volta ao site publico do SuperVitrine.',
        ...(mobile ? { before: async () => { ensureSidebarClosed(); await sleep(300); } } : { before: async () => { await sleep(100); } }),
    });

    // Desktop only: reset tour button
    if (!mobile) {
        steps.push({
            target: '[data-testid="reset-tour-btn"]',
            title: 'Refazer o Tour',
            content: 'Quer rever este tour? Clique aqui a qualquer momento.',
            before: async () => { await sleep(100); },
        });
    }

    // Final step
    const finalContent = profile === 'empresa'
        ? 'Estamos felizes em ter voce por aqui!\n\nExplore todas as funcionalidades, gerencie sua equipe e aproveite o maximo do SuperVitrine.'
        : profile === 'colaborador'
            ? 'Voce ja conhece as principais funcoes do seu painel.\n\nRegistre seu ponto, acompanhe seu historico e fique por dentro dos avisos.'
            : 'Voce ja conhece as principais areas do painel.\n\nExplore, configure e gerencie o SuperVitrine. Qualquer duvida, estamos a disposicao!';

    steps.push({
        virtual: true, isFinal: true,
        title: profile === 'colaborador' ? 'Tudo certo!' : 'Tudo pronto!',
        content: finalContent,
        buttonText: 'Comecar agora',
        ...(mobile ? { before: async () => { ensureSidebarClosed(); await sleep(200); } } : {}),
    });

    return steps;
}

// ==================== SITE TOUR ====================

export function SiteTour() {
    const [active, setActive] = useState(false);
    const [stepIdx, setStepIdx] = useState(0);
    const { canAccessSystem, isAuthenticated } = useAuth();
    const navigate = useNavigate();

    const steps = useMemo(
        () => buildSiteSteps(navigate, isAuthenticated, canAccessSystem),
        [navigate, isAuthenticated, canAccessSystem]
    );

    useEffect(() => {
        if (!localStorage.getItem(SK_SITE)) {
            const token = localStorage.getItem('token') || sessionStorage.getItem('token');
            if (!token) return;
            const t = setTimeout(() => setActive(true), 2500);
            return () => clearTimeout(t);
        }
    }, []);

    const close = useCallback(() => {
        setActive(false);
        localStorage.setItem(SK_SITE, 'true');
        forceCloseDropdown();
        closeDropdowns();
        if (window.location.pathname !== '/') navigate('/');
    }, [navigate]);

    const finish = useCallback(() => {
        setActive(false);
        localStorage.setItem(SK_SITE, 'true');
        sessionStorage.setItem('sv_tour_just_completed', '1');
        forceCloseDropdown();
        closeDropdowns();
        if (window.location.pathname !== '/') navigate('/');
        window.dispatchEvent(new CustomEvent(TOUR_DONE_EVENT, { detail: { type: 'site' } }));
    }, [navigate]);

    if (!active) return null;
    return <TourTooltip steps={steps} currentStep={stepIdx} onNext={() => setStepIdx(s => s + 1)} onPrev={() => setStepIdx(s => Math.max(0, s - 1))} onClose={close} onFinish={finish} />;
}

// ==================== SYSTEM TOUR (Backend-Persisted) ====================

export function SystemTour() {
    const [active, setActive] = useState(false);
    const [stepIdx, setStepIdx] = useState(0);

    const path = window.location.pathname;
    const profile = path.startsWith('/sistema/empresa') ? 'empresa' : path.startsWith('/sistema/colaborador') ? 'colaborador' : 'admin';

    const steps = useMemo(() => buildSystemTourSteps(profile), [profile]);

    useEffect(() => {
        let cancelled = false;
        const token = localStorage.getItem('token');
        if (!token) return;

        const activateThroughQueue = async () => {
            window.__sv_main_tour_active = true;
            // Queue: main tour waits for category onboarding to finish first.
            await OverlayQueue.acquire('main_tour', OVERLAY_PRIORITY.MAIN_TOUR);
            if (cancelled) {
                OverlayQueue.release('main_tour');
                window.__sv_main_tour_active = false;
                return;
            }
            setActive(true);
        };

        const check = async () => {
            try {
                const res = await fetch(`${API}/api/user/main-tour`, { headers: { Authorization: `Bearer ${token}` } });
                if (!res.ok) return;
                const data = await res.json();
                if (cancelled) return;
                // Show if never seen OR version changed
                if (!data.seen || data.version !== TOUR_VERSION) {
                    activateThroughQueue();
                }
            } catch {
                // Fallback to localStorage
                if (!localStorage.getItem('sv_tour_system_done')) {
                    activateThroughQueue();
                }
            }
        };
        check();
        return () => { cancelled = true; };
    }, []);

    const dismiss = useCallback(async () => {
        setActive(false);
        window.__sv_main_tour_active = false;
        localStorage.setItem('sv_tour_system_done', 'true');
        if (isMobile()) ensureSidebarClosed();
        try {
            const token = localStorage.getItem('token');
            await fetch(`${API}/api/user/main-tour/dismiss`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ version: TOUR_VERSION }),
            });
        } catch { /* silent */ }
        // Release queue so the next overlay (tab tour, then biometric) can open.
        OverlayQueue.release('main_tour');
    }, []);

    const close = useCallback(() => { dismiss(); }, [dismiss]);

    const finish = useCallback(() => {
        dismiss();
        sessionStorage.setItem('sv_tour_just_completed', '1');
        window.dispatchEvent(new CustomEvent(TOUR_DONE_EVENT, { detail: { type: 'system' } }));
    }, [dismiss]);

    if (!active) return null;
    return <TourTooltip steps={steps} currentStep={stepIdx} onNext={() => setStepIdx(s => s + 1)} onPrev={() => setStepIdx(s => Math.max(0, s - 1))} onClose={close} onFinish={finish} />;
}

// ==================== TOUR RESET ====================

export function useTourReset(type = 'site') {
    return async () => {
        if (type === 'system') {
            localStorage.removeItem('sv_tour_system_done');
            try {
                const token = localStorage.getItem('token');
                await fetch(`${API}/api/user/main-tour`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
            } catch { /* silent */ }
        } else {
            localStorage.removeItem('sv_tour_site_done');
        }
        window.location.reload();
    };
}
